/**
 * Language Context
 * Provides language selection (English/Spanish) throughout the app
 * Persists language preference to localStorage
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language } from '../lib/voiceManager';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = 'wonderpath-language-preference';

/** Query string busts favicon cache (Cursor Simple Browser / some tabs cache <link rel="icon"> hard). */
export const WONDERPATH_FAVICON_HREF = '/wonderpath-favicon.svg?v=3';

/** Tab title + favicon + html lang — call on boot and whenever language changes. */
export function applyWonderpathDocumentTab(language: Language): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language === 'es' ? 'es' : 'en';
  document.title =
    language === 'es'
      ? 'Wonderpath EcoGames · Primera infancia'
      : 'Wonderpath EcoGames · Early Childhood';
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon) icon.href = WONDERPATH_FAVICON_HREF;
}

// Get initial language from localStorage or default to 'en'
export function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'en';

  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'en' || stored === 'es') {
      return stored;
    }
  } catch (error) {
    console.error('Error reading language from localStorage:', error);
  }

  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // Persist language changes to localStorage
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);

    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch (error) {
      console.error('Error saving language to localStorage:', error);
    }
  };

  // Browser tab: localized title + html lang + favicon (keeps embedded previews in sync)
  useEffect(() => {
    applyWonderpathDocumentTab(language);
  }, [language]);

  // Sync language from localStorage on mount (in case it was changed in another tab)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY && (e.newValue === 'en' || e.newValue === 'es')) {
        setLanguageState(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
